import type { SupabaseClient } from '@supabase/supabase-js';

export type CommandRoute = 'Local' | 'Cloud';

export interface RoutedCommandResult {
  readonly route: CommandRoute;
  readonly data: unknown;
  readonly error: string | null;
}

/**
 * The local Command origin is served by Brunel Node itself. A detached/stale
 * local tab may fall back to the canonical cloud RPC with the SAME requestId;
 * that RPC owns replay protection and never creates a second command.
 */
export async function postMainCommandByRoute(
  client: SupabaseClient,
  request: Record<string, unknown>,
): Promise<RoutedCommandResult> {
  if (window.location.origin === 'http://127.0.0.1:17533') {
    const session = await client.auth.getSession();
    const token = session.data.session?.access_token;
    if (!token) return { route: 'Local', data: null, error: 'Sign in to send a Command message.' };
    try {
      const response = await fetch('/local/command', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(request),
        signal: AbortSignal.timeout(20_000),
      });
      const data: unknown = await response.json();
      if (response.status !== 404) {
        return { route: 'Local', data, error: response.ok ? null : `Local Command answered ${response.status}` };
      }
    } catch {
      // The local host disappeared after the tab loaded. The canonical RPC
      // dedupes by requestId if it had accepted the request before disconnect.
    }
  }
  const response = await client.rpc('post_main_command_message', { request });
  return { route: 'Cloud', data: response.data, error: response.error?.message ?? null };
}
